import Image from "next/image";
import Link from "next/link";
import { Button } from "@/v2-components/ui/button";
import { useTrans } from "@/utils/useTrans";
import ComponentErrorBoundary from "@/v2-components/pages/ComponentErrorBoundary";

function AppIcon() {
  return (
    <div className="relative size-[80px] shrink-0 overflow-hidden rounded-[18px]">
      <Image
        src="/images/software/live/install-app-glass.png"
        alt=""
        fill
        className="object-cover"
        aria-hidden
      />
      <svg
        width="40"
        height="36"
        viewBox="0 0 40 36"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        aria-hidden="true"
      >
        <path
          d="M22.3219 34.772L2.79372 19.4338C-1.6348 15.9569 -0.627714 8.96981 4.60781 6.89039L21.0147 0.384694C23.2556 -0.504574 25.81 0.184108 27.3106 2.07631L38.8154 16.6189C40.4361 18.6648 40.3894 21.5734 38.7021 23.5658L29.8717 34.003C27.9709 36.2496 24.6429 36.5839 22.3352 34.772H22.3219ZM6.5086 15.3953L24.316 29.3762C25.083 29.9779 26.1968 29.871 26.8304 29.1221L33.5599 21.1655C34.1201 20.5036 34.1402 19.5341 33.5999 18.8521L23.8625 6.54271C23.3623 5.91421 22.5086 5.68019 21.7616 5.98107L7.02215 11.8315C5.53486 12.4199 5.24808 14.4124 6.5086 15.402V15.3953Z"
          fill="#FFDD00"
        />
      </svg>
    </div>
  );
}

function Desktop({
  heading,
  desc,
  cta,
}: {
  heading: string;
  desc: string;
  cta: string;
}) {
  return (
    <div className="relative hidden overflow-hidden rounded-[16px] md:flex md:flex-row md:items-center md:justify-between md:px-[64px] md:py-[40px]">
      <div className="absolute inset-0">
        <Image
          src="/images/software/live/install-section-bg.png"
          alt=""
          fill
          className="pointer-events-none object-cover"
          aria-hidden
        />
      </div>
      <div className="relative flex flex-row items-center gap-[40px]">
        <AppIcon />
        <div className="flex flex-col gap-[8px]">
          <h2 className="text-[28px] font-400 leading-9 text-coolgray-90">
            {heading}
          </h2>
          <p className="max-w-[460px] text-[14px] font-400 leading-[18px] tracking-[0.25px] text-coolgray-70">
            {desc}
          </p>
        </div>
      </div>
      <Button
        variant="blue-fill"
        size="sm"
        shape="pill"
        asChild
        className="relative shrink-0 px-[24px]"
      >
        <Link href="/games/pc/software">{cta}</Link>
      </Button>
    </div>
  );
}

function Mobile({
  heading,
  desc,
  cta,
}: {
  heading: string;
  desc: string;
  cta: string;
}) {
  return (
    <div className="relative flex overflow-hidden md:hidden">
      <div className="absolute inset-0">
        <Image
          src="/images/software/live/install-section-bg.png"
          alt=""
          fill
          className="pointer-events-none object-cover"
          aria-hidden
        />
      </div>
      <div className="relative flex w-full flex-col items-center justify-center gap-8 px-4 py-[40px] text-center">
        <AppIcon />
        <div className="flex w-full flex-col gap-3">
          <h2 className="text-[28px] font-400 leading-[36px] text-coolgray-90">
            {heading}
          </h2>
          <p className="text-[14px] font-400 leading-[18px] tracking-[0.25px] text-coolgray-70">
            {desc}
          </p>
        </div>
        <Button
          variant="blue-fill"
          size="sm"
          shape="pill"
          asChild
          className="px-[24px]"
        >
          <Link href="/games/pc/software">{cta}</Link>
        </Button>
      </div>
    </div>
  );
}

export default function LiveInstallSection() {
  const { t } = useTrans(["software"]);
  return (
    <ComponentErrorBoundary>
      <section className="w-full">
        <div className="mx-auto w-full max-w-[1440px]">
          <Desktop
            heading={t("live_install_heading")}
            desc={t("live_install_desc")}
            cta={t("live_install_cta")}
          />
          <Mobile
            heading={t("live_install_heading")}
            desc={t("live_install_desc")}
            cta={t("live_install_cta")}
          />
        </div>
      </section>
    </ComponentErrorBoundary>
  );
}
